import * as React from "react";
import * as Chart from "../../ChartZone.styles";
import { DropdownMenu } from "@conserveit/blocks";
import {
  VIEW_BY_OPTIONS,
  ORDER_BY_OPTIONS,
} from "../../../../context/reports/comfort/constants";
import {
  ViewBy,
  RankDir,
  Zones,
} from "../../../../context/reports/comfort/types";
import { ChartLoader } from "../../../Charts/components/ChartLoader";
import { ZonesDiscomfortBarChart } from "../../../Charts/components/ZonesDiscomfortBarChart";
import { ComfortSummaryDataGrid } from "./ComfortSummaryDataGrid";
import { getZonesChartConfig } from "../zonesChartConfig";
import { useTrackedActions } from "../hooks/useTrackedActions";
import { Margin, GridMargin } from "../../../Layout/Page";
import { NoResult } from "../../";
import { COLORS_RAW } from "../../../.,/../AppChrome/App.constants";

interface Props {
  viewBy: ViewBy;
  rankDir: RankDir;
  zonesLoading: boolean;
  current: { from: string; to: string };
  siteId: string;
  zones?: Zones;
}

function getSubTitle(viewBy: ViewBy) {
  return viewBy === "hoursDiscomfort" ? (
    <>
      Showing total hours identified as <em>uncomfortable</em> during occupancy
    </>
  ) : (
    <>
      Showing % of occupancy identified as <em>uncomfortable</em>
    </>
  );
}

const trackingTitle = "Individual equip performance";

export const ZonesFrame = React.memo<Props>(
  ({ viewBy, rankDir, zonesLoading, current, siteId, zones }) => {
    const { trackedViewBy, trackedRankDir } = useTrackedActions();

    const chartConfig = React.useMemo(() => {
      if (!zones) return undefined;
      return getZonesChartConfig(
        viewBy,
        rankDir,
        current.from,
        current.to,
        zones
      );
    }, [zones, current.from, current.to, viewBy, rankDir, siteId]);

    const hasData =
      chartConfig && chartConfig.dataSource.categories[0].category.length > 0;

    const discomfortData: { name: string; value: number }[] = [];
    const viewKey =
      viewBy === "pctDiscomfort" ? "pctDiscomfort" : "hoursDiscomfort";
    if (zones) {
      const orderedKeys = Object.keys(zones.current.zones).sort(
        (a, b) =>
          (rankDir == "worst" ? 1 : -1) *
          (zones.current.zones[b][viewKey]! - zones.current.zones[a][viewKey]!)
      );

      orderedKeys.forEach((key) => {
        discomfortData.push({
          name: zones.current.zones[key].name,
          value: zones.current.zones[key][viewKey]!,
        });
      });
    }

    return (
      <Chart.Container>
        <Chart.Block>
          <Chart.Header>
            <Chart.Heading>
              <Chart.Title>{trackingTitle}</Chart.Title>
              <Chart.Subtitle>{getSubTitle(viewBy)}</Chart.Subtitle>
            </Chart.Heading>

            <Chart.Controls>
              <DropdownMenu
                trigger="Rank by"
                options={VIEW_BY_OPTIONS}
                onSelect={trackedViewBy(trackingTitle)}
                value={viewBy}
              />
              <DropdownMenu
                trigger="Order by"
                options={ORDER_BY_OPTIONS}
                onSelect={trackedRankDir(trackingTitle)}
                value={rankDir}
                right
              />
            </Chart.Controls>
          </Chart.Header>

          <Chart.Content>
            {zonesLoading ? (
              <ChartLoader />
            ) : hasData ? (
              <Margin>
                <ZonesDiscomfortBarChart
                  data={discomfortData}
                  color={COLORS_RAW.RED_002}
                  valueSuffix={
                    viewBy === "pctDiscomfort"
                      ? "% of hours uncomfortable"
                      : " hours"
                  }
                />
              </Margin>
            ) : (
              <NoResult />
            )}
          </Chart.Content>

          {hasData && (
            <Chart.GridContainer>
              {!zonesLoading && zones && (
                <GridMargin>
                  <ComfortSummaryDataGrid
                    zones={zones.current.zones}
                    trackingTitle={trackingTitle}
                  />
                </GridMargin>
              )}
            </Chart.GridContainer>
          )}
        </Chart.Block>
      </Chart.Container>
    );
  },
  (p, n) =>
    p.viewBy === n.viewBy &&
    p.rankDir === n.rankDir &&
    p.zonesLoading === n.zonesLoading
);
